/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Attribute, ComponentFactoryResolver, ComponentRef, Directive, EventEmitter, Injector, OnDestroy, Output, ReflectiveInjector, ResolvedReflectiveProvider, ViewContainerRef} from '@angular/core';
import {RouterOutletMap} from '../router_outlet_map';
import {ActivatedRoute} from '../router_state';
import {PRIMARY_OUTLET} from '../shared';
/**
 * \@whatItDoes Acts as a placeholder that Angular dynamically fills based on the current router
 * state.
 * 
 * \@howToUse 
 * 
 * ```
 * <router-outlet></router-outlet>
 * <router-outlet name='left'></router-outlet>
 * <router-outlet name='right'></router-outlet>
 * ```
 * 
 * A router outlet will emit an activate event any time a new component is being instantiated,
 * and a deactivate event when it is being destroyed.
 * 
 * ```
 * <router-outlet
 *   (activate)='onActivate($event)'
 *   (deactivate)='onDeactivate($event)'></router-outlet>
 * ```
 * \@ngModule RouterModule
 * 
 * \@stable
 */
export class RouterOutlet implements OnDestroy {
private activated: ComponentRef<any>;
private _activatedRoute: ActivatedRoute;
public outletMap: RouterOutletMap;

   activateEvents = new EventEmitter<any>();
   deactivateEvents = new EventEmitter<any>();
/**
 * @param {?} parentOutletMap
 * @param {?} location
 * @param {?} resolver
 * @param {?} name
 */
constructor(
private parentOutletMap: RouterOutletMap,
private location: ViewContainerRef,
private resolver: ComponentFactoryResolver,
private name: string) {
    parentOutletMap.registerOutlet(name ? name : PRIMARY_OUTLET, this);
  }
/**
 * @return {?}
 */
ngOnDestroy(): void { this.parentOutletMap.removeOutlet(this.name ? this.name : PRIMARY_OUTLET); }
/**
 * @deprecated since v4 *
 * @return {?}
 */
get locationInjector(): Injector { return this.location.injector; }
/**
 * @deprecated since v4 *
 * @return {?}
 */
get locationFactoryResolver(): ComponentFactoryResolver { return this.resolver; }
/**
 * @return {?}
 */
get isActivated(): boolean { return !!this.activated; }
/**
 * @return {?}
 */
get component(): Object {
    if (!this.activated) throw new Error('Outlet is not activated');
    return this.activated.instance;
  }
/**
 * @return {?}
 */
get activatedRoute(): ActivatedRoute {
    if (!this.activated) throw new Error('Outlet is not activated');
    return this._activatedRoute;
  }
/**
 * @return {?}
 */
detach(): ComponentRef<any> {
    if (!this.activated) throw new Error('Outlet is not activated');
    this.location.detach();
    const /** @type {?} */ r = this.activated;
    this.activated = /** @type {?} */(( null));
    this._activatedRoute = /** @type {?} */(( null));
    return r;
  }
/**
 * @param {?} ref
 * @param {?} activatedRoute
 * @return {?}
 */
attach(ref: ComponentRef<any>, activatedRoute: ActivatedRoute) {
    this.activated = ref;
    this._activatedRoute = activatedRoute;
    this.location.insert(ref.hostView);
  }
/**
 * @return {?}
 */
deactivate(): void {
    if (this.activated) {
      const /** @type {?} */ c = this.component;
      this.activated.destroy();
      this.activated = /** @type {?} */(( null));
      this._activatedRoute = /** @type {?} */(( null));
      this.deactivateEvents.emit(c);
    }
  }
/**
 * @deprecated since v4, use {\@link #activateWith}
 * @param {?} activatedRoute
 * @param {?} resolver
 * @param {?} injector
 * @param {?} providers
 * @param {?} outletMap
 * @return {?}
 */
activate(
      activatedRoute: ActivatedRoute, resolver: ComponentFactoryResolver, injector: Injector,
      providers: ResolvedReflectiveProvider[], outletMap: RouterOutletMap): void {
    if (this.isActivated) {
      throw new Error('Cannot activate an already activated outlet');
    }

    this.outletMap = outletMap;
    this._activatedRoute = activatedRoute;

    const /** @type {?} */ snapshot = activatedRoute._futureSnapshot;
    const /** @type {?} */ component: any = /** @type {?} */(( <any> /** @type {?} */((snapshot._routeConfig)).component));
    const /** @type {?} */ factory = /** @type {?} */(( resolver.resolveComponentFactory(component)));

    const /** @type {?} */ inj = ReflectiveInjector.fromResolvedProviders(providers, injector);

    this.activated = this.location.createComponent(factory, this.location.length, inj, []);
    this.activated.changeDetectorRef.detectChanges();

    this.activateEvents.emit(this.activated.instance);
  }
/**
 * @param {?} activatedRoute
 * @param {?} resolver
 * @param {?} outletMap
 * @return {?}
 */
activateWith(
      activatedRoute: ActivatedRoute, resolver: ComponentFactoryResolver|null,
      outletMap: RouterOutletMap) {
    if (this.isActivated) {
      throw new Error('Cannot activate an already activated outlet');
    }

    this.outletMap = outletMap;
    this._activatedRoute = activatedRoute;

    const /** @type {?} */ snapshot = activatedRoute._futureSnapshot;
    const /** @type {?} */ component = /** @type {?} */(( <any> /** @type {?} */((snapshot._routeConfig)).component));

    resolver = resolver || this.resolver;
    const /** @type {?} */ factory = /** @type {?} */(( resolver.resolveComponentFactory(component)));

    const /** @type {?} */ injector = new OutletInjector(activatedRoute, outletMap, this.location.injector);

    this.activated = this.location.createComponent(factory, this.location.length, injector, []);
    this.activated.changeDetectorRef.detectChanges();

    this.activateEvents.emit(this.activated.instance);
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{selector: 'router-outlet'}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: RouterOutletMap, },
{type: ViewContainerRef, },
{type: ComponentFactoryResolver, },
{type: undefined, decorators: [{ type: Attribute, args: ['name', ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'activateEvents': [{ type: Output, args: ['activate', ] },],
'deactivateEvents': [{ type: Output, args: ['deactivate', ] },],
};
}

function RouterOutlet_tsickle_Closure_declarations() {
/** @type {?} */
RouterOutlet.decorators;
/**
 * @nocollapse
 * @type {?}
 */
RouterOutlet.ctorParameters;
/** @type {?} */
RouterOutlet.propDecorators;
/** @type {?} */
RouterOutlet.prototype.activated;
/** @type {?} */
RouterOutlet.prototype._activatedRoute;
/** @type {?} */
RouterOutlet.prototype.outletMap;
/** @type {?} */
RouterOutlet.prototype.activateEvents;
/** @type {?} */
RouterOutlet.prototype.deactivateEvents;
/** @type {?} */
RouterOutlet.prototype.parentOutletMap;
/** @type {?} */
RouterOutlet.prototype.location;
/** @type {?} */
RouterOutlet.prototype.resolver;
/** @type {?} */
RouterOutlet.prototype.name;
}

class OutletInjector implements Injector {
/**
 * @param {?} route
 * @param {?} map
 * @param {?} parent
 */
constructor(
private route: ActivatedRoute,
private map: RouterOutletMap,
private parent: Injector) {}
/**
 * @param {?} token
 * @param {?=} notFoundValue
 * @return {?}
 */
get(token: any, notFoundValue?: any): any {
    if (token === ActivatedRoute) {
      return this.route;
    }

    if (token === RouterOutletMap) {
      return this.map;
    }

    return this.parent.get(token, notFoundValue);
  }
}

function OutletInjector_tsickle_Closure_declarations() {
/** @type {?} */
OutletInjector.prototype.route;
/** @type {?} */
OutletInjector.prototype.map;
/** @type {?} */
OutletInjector.prototype.parent;
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
